/**
 * コレクションLOOK 用の記事（sections/giraffe-article_collection-look.liquid）のスライダー
 * ・[data-look-slider-content] の中で続けて貼り付けた画像（2枚以上）を、メイン＋サムネイルのスライダーにまとめる
 * ・本文に HTML で書かれたスライダー（ページの LOOK からコピーしたものなど）も、同じ設定で動かす
 * ・見た目と動きは SEASONAL COLLECTION の記事のスライダー（giraffe-article_seasonal-collection.liquid）と同じ
 *   （CSS は season-collection-post.css、Swiper は v11）
 */
(() => {
  const CONTAINER_TAGS = ['DIV', 'SECTION', 'ARTICLE'];
  // 本文に HTML で書かれたスライダーは作り直さない（そのまま動かす）
  const EXISTING_SLIDER = '.collection_slider_block, .swiper-container, .swiper';

  /**
   * 画像だけの要素か（文字が入っていない <p><img></p> や <a><img></a> など）
   */
  const isImageOnly = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.tagName === 'IMG') return true;
    if (node.matches(EXISTING_SLIDER) || node.querySelector(EXISTING_SLIDER)) return false;

    return (
      node.querySelector('img') !== null &&
      node.querySelector('iframe, video') === null &&
      node.textContent.trim() === ''
    );
  };

  /**
   * 空白や空行（画像と画像の間にある <p><br></p> など）か
   */
  const isBlank = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim() === '';
    if (node.nodeType !== Node.ELEMENT_NODE) return true;

    return node.querySelector('img, iframe, video') === null && node.textContent.trim() === '';
  };

  /**
   * スライド1枚分。メインは画像に付いたリンクを残し、サムネイルは画像だけにする
   */
  const createSlide = (image, keepLink) => {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const slideImage = document.createElement('img');
    slideImage.src = image.currentSrc || image.src;
    slideImage.alt = image.alt;
    if (image.getAttribute('width')) slideImage.setAttribute('width', image.getAttribute('width'));
    if (image.getAttribute('height')) slideImage.setAttribute('height', image.getAttribute('height'));

    const link = keepLink ? image.closest('a') : null;

    if (link) {
      const slideLink = document.createElement('a');
      slideLink.href = link.href;
      if (link.target) slideLink.target = link.target;
      if (link.rel) slideLink.rel = link.rel;
      if (link.title) slideLink.title = link.title;
      slideLink.append(slideImage);
      slide.append(slideLink);
    } else {
      slide.append(slideImage);
    }

    return slide;
  };

  /**
   * SEASONAL COLLECTION の記事と同じ構造のスライダーを作る
   */
  const createSliderBlock = (images) => {
    const sliderBlock = document.createElement('div');
    sliderBlock.className = 'collection_slider_block';

    const mainSlider = document.createElement('div');
    mainSlider.className = 'swiper collection_slider';
    const mainWrapper = document.createElement('div');
    mainWrapper.className = 'swiper-wrapper';
    images.forEach((image) => mainWrapper.append(createSlide(image, true)));
    const previousButton = document.createElement('div');
    previousButton.className = 'swiper-button-prev';
    const nextButton = document.createElement('div');
    nextButton.className = 'swiper-button-next';
    mainSlider.append(mainWrapper, previousButton, nextButton);

    const thumbnailSlider = document.createElement('div');
    thumbnailSlider.className = 'swiper collection_slider-thumbs';
    const thumbnailWrapper = document.createElement('div');
    thumbnailWrapper.className = 'swiper-wrapper';
    images.forEach((image) => thumbnailWrapper.append(createSlide(image, false)));
    thumbnailSlider.append(thumbnailWrapper);

    sliderBlock.append(mainSlider, thumbnailSlider);

    return sliderBlock;
  };

  /**
   * 本文の中で続けて並んだ画像をスライダーに置き換える（1枚だけの画像はそのまま）
   */
  const buildSliders = (content) => {
    let group = [];

    const flushGroup = () => {
      // 最後の空行はスライダーに含めず残す
      while (group.length && isBlank(group[group.length - 1])) group.pop();

      const images = group
        .filter(isImageOnly)
        .flatMap((node) => (node.tagName === 'IMG' ? [node] : [...node.querySelectorAll('img')]));

      if (images.length >= 2) {
        group[0].before(createSliderBlock(images));
        group.forEach((node) => node.remove());
      }

      group = [];
    };

    [...content.childNodes].forEach((node) => {
      if (isImageOnly(node)) {
        group.push(node);
      } else if (group.length && isBlank(node)) {
        group.push(node);
      } else {
        flushGroup();

        // HTML で書かれた本文（<div> で囲まれている）は、その中の画像もまとめる
        if (CONTAINER_TAGS.includes(node.nodeName) && !node.matches(EXISTING_SLIDER) && node.querySelector('img')) {
          buildSliders(node);
        }
      }
    });

    flushGroup();
  };

  /**
   * スライドの中に入ってしまった文章やボタンを、スライダーの後ろへ出す
   * （HTML で書かれたスライダーをエディタで編集すると、1枚目のスライドに文章が入ってしまうことがあるため）
   */
  const moveTextOutOfSlides = (sliderBlock) => {
    const movedNodes = [];

    sliderBlock.querySelectorAll('.collection_slider .swiper-slide').forEach((slide) => {
      [...slide.childNodes].forEach((node) => {
        // 画像の上に余白ができないよう、改行は消す
        if (node.nodeName === 'BR') {
          node.remove();
          return;
        }

        if (isImageOnly(node) || isBlank(node)) return;

        movedNodes.push(node);
      });

      // 文章だけのスライドは、文章を出すと空になるので消す
      if (!slide.querySelector('img')) slide.remove();
    });

    sliderBlock.after(...movedNodes);
  };

  const initializeCollectionSlider = () => {
    if (typeof Swiper === 'undefined') {
      return false;
    }

    document.querySelectorAll('[data-look-slider-content]:not([data-look-slider-built])').forEach((content) => {
      buildSliders(content);
      content.querySelectorAll('.collection_slider_block').forEach(moveTextOutOfSlides);

      // HTML で書かれたスライダーは古い書き方（.swiper-container）なので、Swiper 11 の CSS が効くよう .swiper を足す
      content.querySelectorAll('.collection_slider_block .swiper-container').forEach((element) => {
        element.classList.add('swiper');
      });

      content.dataset.lookSliderBuilt = 'true';
    });

    const sliderBlocks = document.querySelectorAll(
      '[data-look-slider-content] .collection_slider_block:not([data-swiper-initialized])'
    );

    sliderBlocks.forEach((sliderBlock) => {
      const mainSliderElement = sliderBlock.querySelector('.collection_slider');
      const thumbnailSliderElement = sliderBlock.querySelector('.collection_slider-thumbs');

      if (!mainSliderElement) {
        return;
      }

      // 設定は SEASONAL COLLECTION の記事のスライダーと同じ
      const thumbnailSlider = thumbnailSliderElement
        ? new Swiper(thumbnailSliderElement, {
            slidesPerView: 'auto',
            spaceBetween: 10,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
            centerInsufficientSlides: true,
          })
        : null;

      new Swiper(mainSliderElement, {
        slidesPerView: 1,
        centeredSlides: true,
        rewind: true,
        navigation: {
          nextEl: mainSliderElement.querySelector('.swiper-button-next'),
          prevEl: mainSliderElement.querySelector('.swiper-button-prev'),
        },
        ...(thumbnailSlider && {
          thumbs: {
            swiper: thumbnailSlider,
          },
        }),
      });

      sliderBlock.dataset.swiperInitialized = 'true';
    });

    return true;
  };

  const waitForSwiper = (attempt = 0) => {
    if (initializeCollectionSlider()) {
      return;
    }

    if (attempt >= 50) {
      console.warn('Swiperを読み込めなかったため、コレクションスライダーを初期化できませんでした。');
      return;
    }

    window.setTimeout(() => {
      waitForSwiper(attempt + 1);
    }, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForSwiper(), { once: true });
  } else {
    waitForSwiper();
  }

  document.addEventListener('shopify:section:load', () => {
    waitForSwiper();
  });
})();
